import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LIBRARIES } from '@shared/data/libraries'
import { useAuth } from '@shared/state/AuthContext'
import { useSelection } from '@shared/state/SelectionContext'
import { libraryRef, sendToDesignWorks, sendToMiroQueue, trashPictures } from '@shared/services'
import { putClip } from '@shared/services/projectClip'
import { BulkTagBar } from './BulkTagBar'
import { ClientBar } from './ClientBar'
import { ReportButton } from '@shared/components/ReportButton'
import { TrashButton } from '@shared/components/TrashButton'
import { reportGroups } from '@shared/state/assetLibraries'
import type { MiroSent } from '../hooks/useMiroSendProgress'
import { useMiroSendProgress } from '../hooks/useMiroSendProgress'
import { useClipSettled } from '../hooks/useClipSettled'

export function SelectionTray() {
  const { selectedIds, selectedAssets, deselect, clear, count } = useSelection()

  /**
   * The strip shows the first few and says how many more there are.
   *
   * It used to draw one thumbnail per selected image. Selecting a whole library
   * then put a thousand `<img>` elements in a scrolling row at the bottom of
   * every page — and the strip is a reminder of what is held, not a way to
   * review it, so past a screenful the extra nodes cost paint time and give
   * nothing back. Anything can still be dropped individually by narrowing the
   * selection first.
   */
  const THUMB_LIMIT = 48
  const { user } = useAuth()
  // Straight from the selection, which holds the images themselves.
  const assets = selectedAssets
  const shown = assets.length > THUMB_LIMIT ? assets.slice(0, THUMB_LIMIT) : assets
  const hidden = assets.length - shown.length

  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  /** What was last sent to Miro, followed until it is on the board. */
  const [sent, setSent] = useState<MiroSent | null>(null)
  useMiroSendProgress(sent, setStatus, () => setSent(null))
  /** The copy this page made, until a project takes it; then its message goes. */
  const [copied, setCopied] = useState<{ token: string; text: string } | null>(null)
  useClipSettled(copied?.token ?? '', () => {
    setStatus((now) => (now && now.text === copied?.text ? null : now))
    setCopied(null)
  })
  const dismiss = () => {
    setStatus(null)
    setSent(null)
    setCopied(null)
  }
  const [tagging, setTagging] = useState(false)
  // The library whose page this is, for tagging with that library's tags.
  const { pathname } = useLocation()
  const libraryDef = LIBRARIES.find((one) => one.route === pathname)
  /** Internal Library (2026-10-13): every library at once, the tray as on any library page. */
  const internal = pathname === '/internal-library'
  const isLibraryPage = (path: string) => path === '/internal-library' || LIBRARIES.some((one) => one.route === path)
  const library = libraryDef?.key ?? 'audit'

  /*
   * LEAVING A LIBRARY LETS GO (Huanrui, 2026-10-09). The selection used to
   * stay when the page was left, and the tray was back with it -- and with
   * whatever it last said -- on the next library visited. Now leaving any
   * library page, for another library or anywhere else, clears both. The
   * Moodboard is the one exception: it is laid out FROM the selection.
   *
   * ARRIVING AT A LIBRARY STARTS EMPTY (Huanrui, 2026-10-12). The Moodboard
   * exception let a selection out of the library and nothing let go of it
   * there, so it came back with the next library opened. Now opening any
   * library page from anywhere else clears it too.
   */
  const previousPath = useRef(pathname)
  useEffect(() => {
    const was = previousPath.current
    previousPath.current = pathname
    if (was === pathname) return
    const leaving = isLibraryPage(was) && pathname !== '/moodboard'
    const arriving = isLibraryPage(pathname)
    if (!leaving && !arriving) return
    clear()
    setStatus(null)
    setSent(null)
  }, [pathname, clear])

  /**
   * Copy to project: what is selected is written down, and every project page
   * offers to bring it in. Nothing is copied yet -- which project is decided
   * there, when somebody says yes (src/shared/services/projectClip.ts).
   */
  function handleCopyToProject() {
    try {
      const clip = putClip(
        internal ? 'Internal Library' : (libraryDef?.label ?? 'Library'),
        assets.map((asset) => ({
          kind: 'library' as const,
          id: asset.id,
          thumb: asset.thumbnailPath,
          title: asset.title,
          width: asset.width || undefined,
          height: asset.height || undefined,
        })),
      )
      const text = `${assets.length} copied. Open any project and they will be offered to its canvas.`
      setStatus({ kind: 'ok', text })
      setCopied({ token: clip.token, text })
      // Copied means written down: the selection goes, as after sending to
      // Miro, so the next set can be picked (Huanrui, 2026-09-30).
      clear()
    } catch (error) {
      console.error('could not write the copy down', error)
      setStatus({ kind: 'err', text: 'This browser would not keep the copy' })
    }
  }

  async function handleSendToMiro() {
    if (!user) return
    setSending(true)
    setStatus(null)
    try {
      // The queue belongs to this person alone, so two designers sending at the
      // same time never see each other's images.
      const queue = await sendToMiroQueue('web', selectedIds.map(libraryRef))
      // What the server accepted, not what was sent. A queue has a ceiling, and
      // a message that reports the request rather than the answer is how 200
      // images became 100 with nobody any the wiser.
      const queued = queue.items.length
      const dropped = selectedIds.length - queued
      // Sent means handed over: the selection goes at once, so the next one can
      // be picked (Huanrui, 2026-09-30). It used to stay until the panel said
      // the pictures were on the board, and then clear whatever was selected
      // by that time -- which could be a different set, picked meanwhile.
      // Only when all of them fitted; otherwise the rest are still here to send.
      if (dropped <= 0) clear()
      setSent({ at: queue.createdAt, items: queue.items })
      setStatus({
        kind: dropped > 0 ? 'err' : 'ok',
        text:
          dropped > 0
            ? `Only ${queued} could be queued — ${dropped} did not fit. Place these first, then send the rest.`
            : `${queued} image${queued === 1 ? '' : 's'} ready. Open the WonderWorks app on a Miro board to place them.`,
      })
    } catch (error) {
      setStatus({ kind: 'err', text: error instanceof Error ? error.message : 'Could not send to Miro' })
    } finally {
      setSending(false)
    }
  }

  /*
   * The Egnyte Inbox (Huanrui, 2026-10-08): two things only. Send to Design
   * Works puts the pictures there for everyone, with their Project Name and
   * Type; Trash takes them out (restorable for 30 days, then deleted). Either
   * way they leave the Inbox, so the page reads itself again.
   */
  const [inboxBusy, setInboxBusy] = useState<'' | 'send' | 'trash'>('')
  /** The Client bar (ClientBar), above the tray like the Tag bar. */
  const [naming, setNaming] = useState(false)
  const [askTrash, setAskTrash] = useState(false)
  async function inboxAct(kind: 'send' | 'trash') {
    setInboxBusy(kind)
    setStatus(null)
    try {
      const ids = [...selectedIds]
      const done =
        kind === 'send' ? (await sendToDesignWorks(ids)).sent : (await trashPictures('egnyte', ids)).trashed
      clear()
      window.dispatchEvent(new Event('ww:library-changed'))
      window.dispatchEvent(new Event('ww:trash-changed'))
      setStatus({
        kind: 'ok',
        text:
          kind === 'send'
            ? `${done} sent to Design References, with their Client and Project Name. Run Auto Filter there for Format and Type.`
            : `${done} moved to the trash. They can be restored for 30 days.`,
      })
    } catch (error) {
      setStatus({ kind: 'err', text: error instanceof Error ? error.message : 'That did not work' })
    } finally {
      setInboxBusy('')
      setAskTrash(false)
    }
  }

  // Only on the library pages, where pictures are picked (Huanrui,
  // 2026-09-30). The selection itself is kept: going back to a library brings
  // the tray back as it was. Everything above still runs meanwhile, so a send
  // to Miro is still followed and a copy is still noticed being taken.
  if (!libraryDef && !internal) return null

  // Nothing selected any more, but something to say -- just after sending,
  // above all, since sending clears the selection.
  if (count === 0) {
    return status ? (
      <div className="web-tray" role="status">
        <div className={`web-tray__status${status.kind === 'err' ? ' web-tray__status--err' : ''}`}>
          <span>{status.text}</span>
          <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={dismiss}>
            Dismiss
          </button>
        </div>
      </div>
    ) : null
  }

  return (
    <div className="web-tray" role="region" aria-label="Selected images">
      {status ? (
        <div className={`web-tray__status${status.kind === 'err' ? ' web-tray__status--err' : ''}`}>
          <span>{status.text}</span>
          <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={dismiss}>
            Dismiss
          </button>
        </div>
      ) : null}

      {naming && library === 'egnyte' ? (
        <ClientBar
          assetIds={selectedIds}
          onDone={(text, failed) => setStatus({ kind: failed ? 'err' : 'ok', text })}
          onClose={() => setNaming(false)}
        />
      ) : null}

      {tagging ? (
        <BulkTagBar
          library={library}
          assetIds={selectedIds}
          onDone={(text, failed) => setStatus({ kind: failed ? 'err' : 'ok', text })}
          onClose={() => setTagging(false)}
        />
      ) : null}

      <div className="web-tray__row">
        <span className="web-tray__count">
          {count} <span>selected</span>
        </span>

        <div className="web-tray__thumbs">
          {shown.map((asset) => (
            <button
              key={asset.id}
              type="button"
              className="web-tray__thumb"
              title={`Remove ${asset.title}`}
              onClick={() => deselect(asset.id)}
            >
              <img src={asset.thumbnailPath} alt={asset.title} loading="lazy" />
              <span className="web-tray__remove">×</span>
            </button>
          ))}
          {hidden > 0 ? (
            <span className="web-tray__more" title={`${hidden} more selected`}>
              +{hidden}
            </span>
          ) : null}
        </div>

        {library === 'egnyte' ? (
          <div className="web-tray__actions">
            <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={clear}>
              Clear
            </button>
            <button
              type="button"
              className="ww-btn ww-btn--secondary ww-btn--sm"
              aria-expanded={naming}
              disabled={inboxBusy !== ''}
              onClick={() => setNaming((open) => !open)}
            >
              Client
            </button>
            <button
              type="button"
              className={`ww-btn ww-btn--secondary ww-btn--sm${askTrash ? ' web-tray__warn' : ''}`}
              disabled={inboxBusy !== ''}
              onClick={() => {
                if (!askTrash) {
                  setAskTrash(true)
                  window.setTimeout(() => setAskTrash(false), 4000)
                  return
                }
                void inboxAct('trash')
              }}
            >
              {inboxBusy === 'trash' ? 'Moving…' : askTrash ? `Trash ${count}? Click again` : 'Delete to trash'}
            </button>
            <button
              type="button"
              className="ww-btn ww-btn--primary ww-btn--sm"
              disabled={inboxBusy !== ''}
              onClick={() => void inboxAct('send')}
            >
              {inboxBusy === 'send' ? 'Sending…' : 'Send to Design References'}
            </button>
          </div>
        ) : (
        <div className="web-tray__actions">
          <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={clear}>
            Clear
          </button>
          {/* Editing the library's own data, so it follows the same rule as the
              Tag Manager: offered to an administrator, enforced by the database. */}
          {user?.role === 'admin' ? (
            <button
              type="button"
              className="ww-btn ww-btn--secondary ww-btn--sm"
              aria-expanded={tagging}
              onClick={() => setTagging((open) => !open)}
            >
              Tag
            </button>
          ) : null}
          <button
            type="button"
            className="ww-btn ww-btn--primary ww-btn--sm"
            disabled={sending}
            onClick={() => void handleSendToMiro()}
          >
            {sending ? 'Sending…' : 'Send to Miro'}
          </button>
          <button
            type="button"
            className="ww-btn ww-btn--secondary ww-btn--sm"
            onClick={handleCopyToProject}
          >
            Copy to project
          </button>
          {/* The three big libraries: Delete to trash, from this library only
              (2026-10-15; it was Report here). Administrators only. */}
          {(library === 'audit' || library === 'design-works' || library === 'visual-language') && libraryDef ? (
            user?.role === 'admin' ? (
              <TrashButton
                assetIds={selectedIds}
                library={library}
                onDone={(trashed) => {
                  clear()
                  setStatus({
                    kind: 'ok',
                    text: `${trashed} moved to the trash from ${libraryDef.label ?? 'this library'} only. Restorable for 30 days in the Review Queue.`,
                  })
                }}
              />
            ) : null
          ) : internal ? (
            // Internal Library (2026-10-14): each picture to its own library.
            <ReportButton assetIds={selectedIds} groups={reportGroups(selectedIds)} />
          ) : null}
        </div>
        )}
      </div>
    </div>
  )
}
